import { DSCircularButton } from "@components/design-system/molecules";
import {
  faPause,
  faPlay,
  faSpinner,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useSpring, config, animated } from "@react-spring/web";
import moment from "moment";
import React, { useState } from "react";

import AudioAnalyser from "./AudioAnalyser";
import { CancelRecordingModal } from "./CancelRecordingModal";
import { Timer } from "./Timer";
import { UploadingRecordingButton } from "./UploadingRecordingButton";
import { RecordButton } from "../../common/buttons/RecordButton";
import { StopButton } from "../../common/buttons/StopButton";

export function RecordingSection({
  id,
  isRecording,
  isPaused,
  isLoading,
  audio,
  percentCompleted,
  startRecording,
  stopRecording,
  pauseRecording,
  cancelRecording,
  startPausedRecording,
  trackColor,
  alternativeOnClick = null,
  shouldShowCallMode = false,
  beforeStart,
  disabled = false,
}) {
  const [
    shouldShowCancelRecordingModal,
    setShouldShowCancelRecordingModal,
  ] = useState(false);
  const isCurrentlyRecording = isRecording && audio;
  const isCurrentlyUploading =
    isRecording && !audio && percentCompleted !== 100;

  const startRecordingFlow = () => {
    if (alternativeOnClick) {
      alternativeOnClick();
    } else {
      startRecording();
    }
  };

  const handleClick = () => {
    if (beforeStart) {
      beforeStart(startRecordingFlow);
      return;
    }
    startRecordingFlow();
  };

  const fadeAnalyzer = useSpring({
    config: config.default,
    opacity: isCurrentlyRecording && !isPaused ? 1 : 0,
  });
  const fadeTimer = useSpring({
    config: config.default,
    opacity: isCurrentlyRecording ? 1 : 0,
  });
  function renderPausePlayButton() {
    return (
      <div>
        {isPaused ? (
          <DSCircularButton
            size="lg"
            icon={faPlay}
            onClick={() => startPausedRecording()}
            dataTestId="resume-recording-button"
          />
        ) : (
          <DSCircularButton
            size="lg"
            icon={faPause}
            onClick={() => pauseRecording()}
            dataTestId="pause-recording-button"
          />
        )}
      </div>
    );
  }

  function renderNonRecordingStateButtons() {
    if (isCurrentlyUploading) {
      return (
        <UploadingRecordingButton
          percentCompleted={percentCompleted}
          trackColor={trackColor}
        />
      );
    }

    if (isLoading) {
      return (
        <div
          className={`from-indigo-400 to-indigo-800 absolute z-20 w-3/4 h-3/4 cursor-pointer flex text-center rounded-full transition-all bg-linear-to-br place-items-center place-content-center focus:outline-hidden shadow-lg`}
        >
          <FontAwesomeIcon
            icon={faSpinner}
            size="2x"
            color="white"
            className="mx-auto animate-spin"
          />
        </div>
      );
    }

    return (
      <RecordButton
        id={id}
        onClick={handleClick}
        dataTestId="record-button"
        shouldShowCallMode={shouldShowCallMode}
        disabled={disabled}
      />
    );
  }

  return (
    <>
      <CancelRecordingModal
        shouldShowCancelRecordingModal={
          shouldShowCancelRecordingModal
        }
        setShouldShowCancelRecordingModal={
          setShouldShowCancelRecordingModal
        }
        cancelRecording={cancelRecording}
      />
      {isCurrentlyRecording ? (
        <div className="flex flex-row justify-center items-center relative h-full w-full">
          <animated.div
            style={fadeAnalyzer}
            className="absolute z-10 w-96"
          >
            <AudioAnalyser audio={audio} />
          </animated.div>
          <animated.div
            style={fadeTimer}
            className="absolute z-20 self-center -bottom-8 flex flex-row items-end justify-between gap-4"
          >
            {renderPausePlayButton()}
            <Timer start={moment.utc()} isPaused={isPaused} />
            <div
              className="w-16 h-16 flex flex-row items-center justify-center bg-red-500/70 hover:bg-red-600/70  rounded-full cursor-pointer shadow-md"
              onClick={() => setShouldShowCancelRecordingModal(true)}
            >
              <FontAwesomeIcon
                icon={faTimes}
                size="2x"
                color="white"
                className="mx-auto"
              />
            </div>
          </animated.div>
          <StopButton
            isPaused={isPaused}
            onClick={() => stopRecording()}
            dataTestId="stop-button"
          />
        </div>
      ) : (
        renderNonRecordingStateButtons()
      )}
    </>
  );
}
